import React, { useEffect, useMemo, useState } from "react";
import { ActivityCard, PlayButton, ProgressPill, shuffled } from "../shared";

/**
 * AttachedTeaser — "Pronouns That Stick" preview of attached pronouns.
 *
 * Phase 1: cycle the possessive suffixes on the base word and watch the
 * meaning change. Phase 2: match each built word to its meaning. Completes
 * when all pairs are matched.
 */
export default function AttachedTeaser({ data, onComplete, audio }) {
  const [formIdx, setFormIdx] = useState(0);
  const [seen, setSeen] = useState(() => new Set([0]));
  const [phase, setPhase] = useState("builder"); // builder | match
  const [pickedWord, setPickedWord] = useState(null); // form index picked on the left
  const [matched, setMatched] = useState(() => new Set());
  const [wrongPair, setWrongPair] = useState(null);

  const forms = data.forms;
  const form = forms[formIdx];
  const unitLabel = data.unit_label || "endings";
  const allSeen = seen.size >= forms.length;
  const allMatched = matched.size >= forms.length;

  const shuffledMeanings = useMemo(
    () => shuffled(forms.map((f, i) => ({ ...f, formIdx: i }))),
    [forms]
  );

  useEffect(() => {
    if (allMatched) onComplete();
  }, [allMatched, onComplete]);

  const cycle = (delta) => {
    const next = (formIdx + delta + forms.length) % forms.length;
    setFormIdx(next);
    setSeen((prev) => new Set(prev).add(next));
    audio.play(forms[next].full_ar);
  };

  const handleMatch = (meaning) => {
    if (pickedWord === null || matched.has(meaning.formIdx)) return;
    if (meaning.formIdx === pickedWord) {
      setMatched((prev) => new Set(prev).add(pickedWord));
      setPickedWord(null);
    } else {
      setWrongPair(meaning.formIdx);
      setTimeout(() => setWrongPair(null), 600);
      setPickedWord(null);
    }
  };

  return (
    <ActivityCard>
      {phase === "builder" && (
        <>
          <div className="flex items-center justify-end mb-4">
            <ProgressPill>{seen.size}/{forms.length} {unitLabel}</ProgressPill>
          </div>
          <div className="flex items-center justify-center gap-4 sm:gap-6">
            <button
              type="button"
              onClick={() => cycle(1)}
              aria-label="Next ending"
              className="w-11 h-11 rounded-full text-xl transition-[filter] hover:brightness-110"
              style={{ background: "var(--ink)", border: "1px solid var(--panel-line)", color: "var(--parchment)" }}
            >
              ‹
            </button>
            <div className="text-center min-w-[200px]">
              <p dir="rtl" lang="ar" className="font-arabic text-6xl leading-[1.6] m-0 mb-1" style={{ color: "var(--parchment)" }}>
                {(() => {
                  const suffixBare = form.suffix.replace(/^ـ/, "");
                  const stem = form.full_ar.slice(0, form.full_ar.length - suffixBare.length);
                  return (
                    <>
                      {stem}
                      <span style={{ color: "var(--tag-violet)" }}>{suffixBare}</span>
                    </>
                  );
                })()}
              </p>
              <p className="text-base mt-2 mb-0 inline-flex items-center gap-3" style={{ color: "var(--parchment)" }}>
                {form.en}
                {audio.audioAllowed && <PlayButton text={form.full_ar} playingText={audio.playingText} onPlay={audio.play} />}
              </p>
              <p className="text-xs mt-1 mb-0" style={{ color: "var(--parchment-dim)" }}>
                {data.base_ar} ({data.base_en}) + <span dir="rtl" lang="ar" className="font-arabic" style={{ color: "var(--tag-violet)" }}>{form.suffix}</span> = "{form.owner_en}"
              </p>
            </div>
            <button
              type="button"
              onClick={() => cycle(-1)}
              aria-label="Previous ending"
              className="w-11 h-11 rounded-full text-xl transition-[filter] hover:brightness-110"
              style={{ background: "var(--ink)", border: "1px solid var(--panel-line)", color: "var(--parchment)" }}
            >
              ›
            </button>
          </div>
          <div className="flex justify-center mt-6">
            <button
              type="button"
              onClick={() => setPhase("match")}
              disabled={!allSeen}
              className="inline-flex items-center justify-center px-5 py-2.5 min-h-[44px] text-sm font-semibold rounded-lg transition-[filter] hover:brightness-105 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: "var(--gold)", color: "var(--ink)" }}
            >
              {allSeen ? "Match them up" : `See all ${forms.length} ${unitLabel} first`}
            </button>
          </div>
        </>
      )}

      {phase === "match" && (
        <>
          <p className="text-sm mb-4" style={{ color: "var(--parchment-dim)" }}>{data.match_prompt}</p>
          <div className="grid grid-cols-2 gap-3 max-w-lg mx-auto">
            <div className="flex flex-col gap-2">
              {forms.map((f, i) => (
                <button
                  key={f.full_ar}
                  type="button"
                  onClick={() => setPickedWord(matched.has(i) ? null : i)}
                  disabled={matched.has(i)}
                  dir="rtl"
                  lang="ar"
                  className="font-arabic text-2xl px-3 py-2.5 rounded-xl transition-all hover:brightness-110 disabled:cursor-default"
                  style={{
                    background: "var(--ink)",
                    border: pickedWord === i ? "1px solid var(--tag-violet)" : "1px solid var(--panel-line)",
                    color: matched.has(i) ? "var(--teal)" : "var(--parchment)",
                    opacity: matched.has(i) ? 0.65 : 1,
                    boxShadow: pickedWord === i ? "0 0 0 3px var(--violet-bg)" : "none",
                  }}
                >
                  {f.full_ar}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-2">
              {shuffledMeanings.map((meaning) => (
                <button
                  key={meaning.full_ar}
                  type="button"
                  onClick={() => handleMatch(meaning)}
                  disabled={matched.has(meaning.formIdx) || pickedWord === null}
                  className="text-sm px-3 py-2.5 min-h-[48px] rounded-xl transition-all hover:brightness-110 disabled:cursor-default"
                  style={{
                    background: "var(--ink)",
                    border: wrongPair === meaning.formIdx ? "1px solid #dc5a5a" : "1px solid var(--panel-line)",
                    color: matched.has(meaning.formIdx) ? "var(--teal)" : "var(--parchment)",
                    opacity: matched.has(meaning.formIdx) ? 0.65 : pickedWord === null ? 0.7 : 1,
                  }}
                >
                  {meaning.en}
                </button>
              ))}
            </div>
          </div>
          {pickedWord === null && !allMatched && (
            <p className="text-xs text-center mt-3 mb-0" style={{ color: "var(--parchment-dim)" }}>
              Tap a word, then its meaning.
            </p>
          )}
          {allMatched && (
            <div className="flex items-center gap-2.5 mt-4 px-4 py-3 rounded-xl" style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}>
              <span className="text-lg leading-none" aria-hidden="true">🔗</span>
              <p className="text-sm m-0" style={{ color: "var(--parchment)" }}>
                {data.done_message || "That's the preview — attached pronouns get a full lesson of their own. On to the challenge!"}
              </p>
            </div>
          )}
        </>
      )}
    </ActivityCard>
  );
}
